import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme, type Theme } from "@mui/material/styles";
import { useMemo, type ReactNode } from "react";

import { useAppTheme } from "@/theme";
import {
  ACCENT,
  MARKETING_BREAKPOINTS,
  MID,
  SITE_STEPS,
  marketingPalette,
} from "./tokens";

/**
 * Restating every variant is deliberate: `createTheme` resolves the font and
 * `letterSpacing` into each variant when it builds a theme, so augmenting an
 * already-built one and setting only `fontFamily` leaves Roboto and MUI's tracking.
 */
const VARIANTS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "subtitle1",
  "subtitle2",
  "body1",
  "body2",
  "button",
  "caption",
  "overline",
] as const;

const variantFonts = Object.fromEntries(
  VARIANTS.map((name) => [
    name,
    { fontFamily: marketingPalette.font, letterSpacing: "normal" },
  ])
);

/**
 * What a variant reads, and what one of its breakpoint steps writes. A step
 * sets a custom property rather than `font-size` because stylis emits a
 * variant's `@media` blocks after its flat declarations, so a `font-size` step
 * would out-rank the flat `sx` of any page using the variant.
 */
const reads = (name: string, fontSize: number, lineHeight: string) => ({
  fontSize: `var(--mkt-${name}-size, ${fontSize}px)`,
  lineHeight: `var(--mkt-${name}-leading, ${lineHeight})`,
});

const step = (name: string, fontSize: number, lineHeight: string) => ({
  [`--mkt-${name}-size`]: `${fontSize}px`,
  [`--mkt-${name}-leading`]: lineHeight,
});

/**
 * The measured type ramp. The line-height ratio is not constant across steps,
 * so each step carries explicit px rather than a multiplier, and steps run
 * widest-first so the narrowest wins in emotion's insertion order.
 */
function typeRamp(theme: Theme) {
  const down = (width: number) => theme.breakpoints.down(width);
  const face = { fontFamily: marketingPalette.font, fontWeight: 500 };

  return {
    // The page title on both the listing and the post.
    h1: {
      ...face,
      ...reads("h1", 52, "57.2px"),
      letterSpacing: "-0.04em",
      [down(SITE_STEPS.wide)]: step("h1", 48, "54px"),
      [down(SITE_STEPS.card)]: step("h1", 40, "52px"),
      [down(SITE_STEPS.tiny)]: step("h1", 34, "40.8px"),
    },
    // Section headings inside the article, and the FAQ heading.
    h2: {
      ...face,
      ...reads("h2", 36, "43.2px"),
      letterSpacing: "-0.04em",
      [down(SITE_STEPS.tiny)]: step("h2", 26, "31.2px"),
    },
    // The listing's "Browse the Webshare".
    h3: {
      ...face,
      ...reads("h3", 32, "41.6px"),
      letterSpacing: "-0.04em",
      [down(SITE_STEPS.tiny)]: step("h3", 24, "32px"),
    },
    // The featured card's title, the one place the site uses Semibold.
    h4: {
      ...face,
      fontWeight: 600,
      ...reads("h4", 24, "31.2px"),
      letterSpacing: "-0.03em",
      [down(SITE_STEPS.card)]: step("h4", 19.2, "24.96px"),
    },
    // Sub-headings inside the article.
    h5: {
      ...face,
      ...reads("h5", 22, "26.4px"),
      letterSpacing: "-0.02em",
      [down(SITE_STEPS.tiny)]: step("h5", 20, "24px"),
    },
    // The grid card's title; the only ramp entry with no breakpoint step.
    h6: {
      ...face,
      fontSize: 18.08,
      lineHeight: "23.504px",
      letterSpacing: "-0.03em",
    },
  };
}

/**
 * What the published page applies to the CMS's markup, at the same specificity.
 * That is the point: some posts ship their own `<style>`, and that CSS must
 * win. A raw string keeps `:where(.blog-content) th` at (0,0,1), losing on
 * document order; inside a `styled()` object it would compile to (0,1,1) and
 * beat the author.
 */
const articleBaseline = `
  /* CssBaseline's antialiasing renders every glyph thinner than the published
     site's rasterisation. */
  html, body { -webkit-font-smoothing: auto; -moz-osx-font-smoothing: auto; }
  /* Body links only: the author card and the share row sit outside the wrapper. */
  :where(.blog-content) a { color: ${ACCENT}; text-decoration: none; }
  :where(.blog-content) ul,
  :where(.blog-content) ol { margin-top: 0; margin-bottom: 10px; padding-left: 40px; overflow: hidden; }
  :where(.blog-content) li { margin-top: 0; margin-bottom: 4.8px; }
  /* Below the tablet step the published bullet lists indent half as far; the
     numbered ones keep their 40px. */
  ${MID} { :where(.blog-content) ul { padding-left: 20px; } }
  :where(.blog-content) pre { font-family: monospace; font-size: 1em; overflow: auto; }
  :where(.blog-content) pre > code { display: block; }
  :where(.blog-content) code { font-family: monospace; font-size: 1em; }
  /* The blog template ships a page stylesheet the academy does not, which is why
     .is-blog and not .blog-content carries the roomier box. That rule sits on the
     code element, so it reaches inline chips too; scoping it to pre > code alone
     would not reproduce them. */
  :where(.blog-content) pre > code {
    display: block; overflow-x: auto; padding: inherit; margin: 0;
  }
  :where(.blog-content.is-blog) code {
    width: 100%; max-width: 52rem; border-radius: 8px; margin: 0 !important;
    padding: 15px 30px 30px !important;
  }
  :where(.blog-content.is-blog) pre > code { padding: 15px 30px 30px; }
  /* Webflow's clearfix on an Embed: the empty table either side keeps the
     margins of what was pasted from collapsing through the wrapper. */
  :where(.blog-content) .w-embed::before,
  :where(.blog-content) .w-embed::after { content: " "; display: table; }
  :where(.blog-content) .w-embed::after { clear: both; }
  /* The blog template's own code turns that clearfix off, so on a post a
     pasted block's margins collapse as they would without the wrapper. */
  :where(.blog-content.is-blog) .w-embed::before,
  :where(.blog-content.is-blog) .w-embed::after { display: none; }
  /* Webflow's code block: its code element takes everything from the block,
     so the template's code box above keeps only what it marks important, and
     each line, a span of its own, is a block, a numbered row included. */
  :where(.blog-content) pre.w-code-block code { all: inherit; }
  :where(.blog-content) pre.w-code-block code > span { display: block !important; }
  /* The blog template's highlight.js theme, atom-one-dark 9.15.10, for the
     classes its pass adds to every code element on a post (passCode): the code
     blocks and the code an editor pasted alike. The academy's template loads
     none, so its code shows the code block's own colours alone. At the theme's
     own (0,1,0); its box on .hljs is left out, as the template's padding and
     radius above outrank it there. */
  :where(.blog-content.is-blog) .hljs { color: #abb2bf; background: #282c34; }
  :where(.blog-content.is-blog) .hljs-comment,
  :where(.blog-content.is-blog) .hljs-quote { color: #5c6370; font-style: italic; }
  :where(.blog-content.is-blog) .hljs-doctag,
  :where(.blog-content.is-blog) .hljs-keyword,
  :where(.blog-content.is-blog) .hljs-formula { color: #c678dd; }
  :where(.blog-content.is-blog) .hljs-section,
  :where(.blog-content.is-blog) .hljs-name,
  :where(.blog-content.is-blog) .hljs-selector-tag,
  :where(.blog-content.is-blog) .hljs-deletion,
  :where(.blog-content.is-blog) .hljs-subst { color: #e06c75; }
  :where(.blog-content.is-blog) .hljs-literal { color: #56b6c2; }
  :where(.blog-content.is-blog) .hljs-string,
  :where(.blog-content.is-blog) .hljs-regexp,
  :where(.blog-content.is-blog) .hljs-addition,
  :where(.blog-content.is-blog) .hljs-attribute,
  :where(.blog-content.is-blog) .hljs-meta-string { color: #98c379; }
  :where(.blog-content.is-blog) .hljs-built_in,
  :where(.blog-content.is-blog) .hljs-class .hljs-title { color: #e6c07b; }
  :where(.blog-content.is-blog) .hljs-attr,
  :where(.blog-content.is-blog) .hljs-variable,
  :where(.blog-content.is-blog) .hljs-template-variable,
  :where(.blog-content.is-blog) .hljs-type,
  :where(.blog-content.is-blog) .hljs-selector-class,
  :where(.blog-content.is-blog) .hljs-selector-attr,
  :where(.blog-content.is-blog) .hljs-selector-pseudo,
  :where(.blog-content.is-blog) .hljs-number { color: #d19a66; }
  :where(.blog-content.is-blog) .hljs-symbol,
  :where(.blog-content.is-blog) .hljs-bullet,
  :where(.blog-content.is-blog) .hljs-link,
  :where(.blog-content.is-blog) .hljs-meta,
  :where(.blog-content.is-blog) .hljs-selector-id,
  :where(.blog-content.is-blog) .hljs-title { color: #61aeee; }
  :where(.blog-content.is-blog) .hljs-emphasis { font-style: italic; }
  :where(.blog-content.is-blog) .hljs-strong { font-weight: bold; }
  :where(.blog-content.is-blog) .hljs-link { text-decoration: underline; }
  :where(.blog-content) table { border-collapse: collapse; border-spacing: 0; }
  :where(.blog-content) th,
  :where(.blog-content) td { padding: 0; }

  /* Keyed off the article class because the published site scopes it the same
     way: its contents-rail script writes this onto the post, never the listing. */
  html:has(.blog-content) { scroll-behavior: smooth; }
`;

/**
 * The provider tree for public marketing routes: the theme and nothing else.
 * Not `AppShell`, whose `RouteGuard` would bounce logged-out readers to the
 * login page. Light mode is hard-coded because reading the colour-mode cookie
 * would pull the session helpers into a page that has no session.
 */
/**
 * The marketing theme over the app's. The site's steps go in with the base
 * rather than with the overrides: MUI builds `breakpoints.up/down` from the
 * first argument alone, and only deep-merges the rest.
 */
export function marketingTheme(appTheme: Theme): Theme {
  return createTheme(
    { ...appTheme, breakpoints: { values: MARKETING_BREAKPOINTS } },
    {
      typography: {
        fontFamily: marketingPalette.font,
        ...variantFonts,
        ...typeRamp(appTheme),
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: articleBaseline,
        },
        MuiButtonBase: {
          defaultProps: { disableRipple: true },
          styleOverrides: {
            // `ButtonBase` clears the focus ring with `outline: 0`, which
            // leaves nothing to see once the ripple is gone.
            root: {
              "&:focus-visible": { outline: "revert", outlineOffset: "2px" },
            },
          },
        },
        // The published controls repaint on hover with no easing.
        MuiIconButton: { styleOverrides: { root: { transition: "none" } } },
        MuiButton: { styleOverrides: { root: { transition: "none" } } },
      },
      palette: {
        primary: { main: ACCENT },
        text: {
          primary: marketingPalette.ink,
          secondary: marketingPalette.inkSubtle,
        },
        // The published pages sit on white; the app's default surface is grey.
        background: { default: "#FFFFFF" },
      },
    }
  );
}

export default function MarketingShell({ children }: { children: ReactNode }) {
  const appTheme = useAppTheme("light");

  const theme = useMemo(() => marketingTheme(appTheme), [appTheme]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
